import React, { useEffect, useState, useRef, useMemo } from 'react';
import { DiceRollResult, ActionDifficulty, TriggeredEffect } from '../types';
import Dice3D from './Dice3D';
import DiceResultBreakdown from './DiceResultBreakdown';
import NextButton from './NextButton';
import { useSoundEffects } from '../context/SoundEffectsContext';
import { pickPercyReaction, DiceOutcome } from './percyReactions';
import '../styles/DiceRollPanel.css';

interface DiceRollPanelProps {
  diceRollResult: DiceRollResult;
  onAnimationComplete: () => void;
  showResult: boolean;
  showContinueButton?: boolean;
  onContinue?: () => void;
  isLoadingContinue?: boolean;
  triggeredEffects?: TriggeredEffect[];
}

const DIFFICULTY_LABELS: Record<ActionDifficulty, string> = {
  trivial: 'Trivial',
  easy: 'Easy',
  challenging: 'Challenging',
  very_challenging: 'Very Challenging',
  impossible: 'Impossible',
};

const DIFFICULTY_COLORS: Record<ActionDifficulty, string> = {
  trivial: '#28a745',
  easy: '#5cb85c',
  challenging: '#7c4dff',  // Purple instead of orange
  very_challenging: '#dc3545',
  impossible: '#6c757d',
};

const DiceRollPanel: React.FC<DiceRollPanelProps> = ({
  diceRollResult,
  onAnimationComplete,
  showResult,
  showContinueButton = false,
  onContinue,
  isLoadingContinue = false,
  triggeredEffects = []
}) => {
  const [animationPhase, setAnimationPhase] = useState<'rolling' | 'showing-result'>('rolling');
  const { playSound } = useSoundEffects();
  const hasPlayedSound = useRef(false);

  const isTrivialOrImpossible = diceRollResult.difficulty === 'trivial' ||
                                 diceRollResult.difficulty === 'impossible';

  const hasRolls = diceRollResult.rolls && diceRollResult.rolls.length > 0;

  useEffect(() => {
    if (animationPhase === 'rolling' && !isTrivialOrImpossible && !hasPlayedSound.current) {
      playSound('roll_dice');
      hasPlayedSound.current = true;
    }
  }, [animationPhase, isTrivialOrImpossible, playSound]);

  // Calculate animation duration based on number of dice + stagger
  const baseAnimationDuration = isTrivialOrImpossible ? 500 : 2500;

  useEffect(() => {
    if (isTrivialOrImpossible) {
      const timer = setTimeout(() => {
        setAnimationPhase('showing-result');
        onAnimationComplete();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isTrivialOrImpossible, onAnimationComplete]);

  const handleDiceAnimationComplete = () => {
    setAnimationPhase('showing-result');
    onAnimationComplete();
  };

  const isSuccess = diceRollResult.result === 'Success';
  const difficultyLabel = DIFFICULTY_LABELS[diceRollResult.difficulty];
  const difficultyColor = DIFFICULTY_COLORS[diceRollResult.difficulty];

  const autoOutcome: DiceOutcome = isSuccess ? 'auto_success' : 'auto_failure';
  const autoPercySrc = useMemo(() => pickPercyReaction(autoOutcome), [autoOutcome]);

  return (
    <div className="dice-roll-panel">
      {/* Header with difficulty and target */}
      <div className="dice-header">
        <span
          className="difficulty-badge"
          style={{ backgroundColor: difficultyColor }}
        >
          {difficultyLabel}
        </span>
        {!isTrivialOrImpossible && diceRollResult.target_needed > 0 && (
          <span className="target-text">
            Need <strong>{diceRollResult.target_needed}</strong> or higher
          </span>
        )}
        {isTrivialOrImpossible && (
          <span className="auto-text">
            {diceRollResult.difficulty === 'trivial'
              ? 'Auto-Success!'
              : 'Impossible!'}
          </span>
        )}
      </div>

      {/* Dice Animation Area */}
      {animationPhase === 'rolling' && !isTrivialOrImpossible && hasRolls && (
        <div className="dice-rolling-content">
          <h2 className="dice-panel-title">Rolling the dice...</h2>
          <div className="dice-container">
            {diceRollResult.rolls.map((value, index) => (
              <Dice3D
                key={index}
                value={value}
                diceIndex={index}
                duration={baseAnimationDuration}
                onComplete={index === diceRollResult.rolls.length - 1 ? handleDiceAnimationComplete : undefined}
              />
            ))}
          </div>
        </div>
      )}

      {/* Result Display */}
      {(animationPhase === 'showing-result' || isTrivialOrImpossible) && showResult && (
        <div className="dice-result-content">
          {/* For trivial/impossible, show simple message */}
          {isTrivialOrImpossible ? (
            <div className={`result-message ${isSuccess ? 'success' : 'failure'}`}>
              <img
                className={`percy-reaction ${isSuccess ? 'success' : 'failure'}`}
                src={autoPercySrc}
                alt=""
              />
              <div className="result-text">
                {isSuccess ? 'Success!' : 'Failure!'}
              </div>
              <div className="auto-result-text">
                {diceRollResult.difficulty === 'trivial'
                  ? "This action was so easy, success was guaranteed!"
                  : "This action was impossible from the start."}
              </div>
            </div>
          ) : (
            /* For normal rolls, show the breakdown */
            hasRolls && (
              <DiceResultBreakdown
                rolls={diceRollResult.rolls}
                total={diceRollResult.total}
                target={diceRollResult.target_needed}
                modifiers={diceRollResult.modifiers_applied || []}
                success={isSuccess}
                triggeredEffects={triggeredEffects}
              />
            )
          )}

          {/* Continue button or loading state */}
          <div className="dice-continue-button-container">
            {showContinueButton && onContinue ? (
              <NextButton
                onClick={onContinue}
                text="Continue"
                loading={isLoadingContinue}
                variant="primary"
                size="large"
              />
            ) : (
              <div className="loading-feedback">
                <div className="loading-spinner-dice"></div>
                <p className="loading-text">Time Traveling...</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DiceRollPanel;
